import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronLeftLine, EditLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { Breadcrumb } from "@mirage/mosaics/BaseProfilePage/Breadcrumb";
import {
  canUserEditUserProfile,
  getEditUserProfilePageHref,
  getUserProfilePageHref,
} from "@mirage/mosaics/UserProfilePage/utils";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import i18n from "@mirage/translations";
import { useNavigate } from "react-router-dom";

import type { UserInfo } from "@mirage/mosaics/UserProfilePage/types";

export const UserProfileHeader = ({
  user,
  isEdit = false,
  showEditButton = false,
  showBackButton = false,
}: {
  user: UserInfo;
  isEdit?: boolean;
  showEditButton?: boolean;
  showBackButton?: boolean;
}) => {
  const navigate = useNavigate();
  const currentAccount = useDropboxAccount();
  const canEdit = canUserEditUserProfile(currentAccount?.email, user.email);

  const handleOnBackClick = () => {
    navigate(-1);
  };

  const breadcrumbPaths = [...user.reporting_line].reverse().map((user) => ({
    children: user.name,
    to: getUserProfilePageHref(user.email) ?? "",
  }));

  const showHeader =
    showBackButton || !!breadcrumbPaths.length || showEditButton;
  if (!showHeader) {
    return null;
  }

  return (
    <Box as="div" display="flex" justifyContent="space-between">
      <Box as="div" display="flex" alignItems="center" style={{ gap: "24px" }}>
        {showBackButton && (
          <Button
            variant="transparent"
            withIconStart={<UIIcon src={ChevronLeftLine} isOpticallyAligned />}
            hasNoUnderline
            onClick={handleOnBackClick}
          >
            <Box
              as={Text}
              display="block"
              variant="label"
              style={{
                textAlign: "center",
                fontSize: "13px",
                letterSpacing: "0.26px",
              }}
              isBold
              color="Text Subtle"
            >
              {i18n.t("back")}
            </Box>
          </Button>
        )}
        {!!breadcrumbPaths.length && <Breadcrumb path={breadcrumbPaths} />}
      </Box>
      {showEditButton &&
        canEdit &&
        (isEdit ? (
          <Split gap="8">
            <Split.Item>
              <Button variant="opacity" onClick={handleOnBackClick}>
                {i18n.t("cancel")}
              </Button>
            </Split.Item>
            <Split.Item>
              <Button variant="primary">{i18n.t("save")}</Button>
            </Split.Item>
          </Split>
        ) : (
          <Button
            variant="opacity"
            withIconStart={<UIIcon src={EditLine} />}
            onClick={() => {
              const editUrl = getEditUserProfilePageHref(user.email);
              if (editUrl) {
                navigate(editUrl);
              }
            }}
          >
            {i18n.t("edit")}
          </Button>
        ))}
    </Box>
  );
};
